import { useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Minus, Plus, MessageCircle, Truck, ShieldCheck, ArrowUpRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { FLAVOR_LIST, PACK, MIN_ORDER_QTY, WHATSAPP, POLICIES_PDF, waLink } from '../lib/products';
import PackImg from '../components/PackImg';
import KrispButton from '../components/KrispButton';
import Footer from '../sections/Footer';

const Field = ({ label, testid, ...rest }) => (
  <label className="block">
    <span className="font-cond text-[10px] tracking-[0.3em] text-cocoa/60">{label}</span>
    <input data-testid={testid} className="k-input mt-1.5 !rounded-2xl" {...rest} />
  </label>
);

export default function CheckoutPage() {
  const [params] = useSearchParams();
  const add = params.get('add');
  const addQty = Math.max(MIN_ORDER_QTY, parseInt(params.get('qty') || MIN_ORDER_QTY, 10) || MIN_ORDER_QTY);
  const [qty, setQty] = useState(() => Object.fromEntries(FLAVOR_LIST.map((f) => [f.slug, add === f.slug ? addQty : 0])));
  const [form, setForm] = useState({ name: '', phone: '', email: '', address: '' });
  const [busy, setBusy] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const total = useMemo(() => Object.values(qty).reduce((a, b) => a + b, 0), [qty]);
  const amount = total * PACK.price;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const bump = (slug, d) => setQty({ ...qty, [slug]: Math.max(0, Math.min(99, qty[slug] + d)) });
  const valid = total >= MIN_ORDER_QTY && form.name.trim() && form.phone.replace(/\D/g, '').length >= 10 && form.email.includes('@') && form.address.trim().length > 5;

  const payNow = async (e) => {
    e.preventDefault();
    if (total < MIN_ORDER_QTY) { toast(`MINIMUM ORDER IS ${MIN_ORDER_QTY} PACKETS`); return; }
    if (!valid) { toast('FILL IN ALL YOUR DETAILS FIRST'); return; }
    if (!agreed) { toast('PLEASE ACCEPT THE TERMS & POLICIES FIRST'); return; }
    setBusy(true);
    let orderId = '';
    try {
      const items = FLAVOR_LIST.filter((f) => qty[f.slug] > 0).map((f) => ({ slug: f.slug, variant: PACK.id, qty: qty[f.slug] }));
      const { data } = await api.post('/orders', { customer: { ...form, city: '', pincode: '' }, items, payment_method: 'whatsapp' });
      orderId = data.id;
    } catch { /* order still goes to WhatsApp */ }
    const lines = FLAVOR_LIST.filter((f) => qty[f.slug] > 0).map((f) => `• ${f.name} 50G × ${qty[f.slug]} = ₹${qty[f.slug] * PACK.price}`).join('\n');
    const msg = `Hi PROKRISP! I'd like to place an order${orderId ? ` (#${orderId})` : ''}.\n\n${lines}\n\nTotal: ₹${amount} (+ shipping charges applicable)\n\nName: ${form.name}\nPhone: ${form.phone}\nEmail: ${form.email}\nAddress: ${form.address}`;
    window.open(waLink(msg), '_blank', 'noopener');
    setBusy(false);
    toast('OPENING WHATSAPP TO CONFIRM YOUR ORDER');
  };

  return (
    <main data-testid="checkout-page" className="bg-cream min-h-screen">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-10 pb-20">
        <p className="font-cond text-xs tracking-[0.4em] text-cocoa/60"><Link to="/products" className="hover:text-cocoa">SHOP</Link> / CHECKOUT</p>
        <h1 className="font-display text-7xl sm:text-9xl text-cocoa leading-none mt-2">CHECK<span className="stroke-text">OUT.</span></h1>
        <p className="text-sm text-cocoa/70 mt-3 max-w-xl">Pick your packets (minimum {MIN_ORDER_QTY}), add your details and hit Pay Now — we confirm your order and payment on WhatsApp at {WHATSAPP.display}.</p>

        <form onSubmit={payNow} noValidate className="grid lg:grid-cols-[1.1fr_1fr] gap-8 mt-10 items-start">
          <div className="space-y-4">
            <div className="rounded-[2rem] bg-white/70 border-2 border-cocoa p-6">
              <p className="font-display text-3xl text-cocoa leading-none">1 · YOUR PACKETS</p>
              <p className="font-cond text-[10px] tracking-[0.3em] text-cocoa/60 mt-1">50G · 15G PROTEIN · ₹{PACK.price} EACH · MIN {MIN_ORDER_QTY} PACKETS</p>
              <div className="mt-5 space-y-3">
                {FLAVOR_LIST.map((f) => (
                  <div key={f.slug} data-testid={`checkout-item-${f.slug}`} className="flex items-center gap-4 rounded-2xl border-2 border-cocoa/15 p-3" style={{ background: `${f.color}14` }}>
                    <PackImg f={f} className="h-20 w-16" />
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-2xl text-cocoa leading-none">{f.name}</p>
                      <p className="font-cond text-[10px] tracking-[0.25em] text-cocoa/60 mt-1">₹{PACK.price} × {qty[f.slug]} = ₹{qty[f.slug] * PACK.price}</p>
                    </div>
                    <div className="flex items-center gap-3 rounded-full border-2 border-cocoa bg-white px-3 py-2">
                      <button type="button" data-testid={`qty-minus-${f.slug}`} aria-label="minus" onClick={() => bump(f.slug, -1)}><Minus size={14} /></button>
                      <span data-testid={`qty-${f.slug}`} className="font-display text-2xl w-7 text-center">{qty[f.slug]}</span>
                      <button type="button" data-testid={`qty-plus-${f.slug}`} aria-label="plus" onClick={() => bump(f.slug, 1)}><Plus size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
              {total < MIN_ORDER_QTY && <p data-testid="min-order-warning" className="mt-4 rounded-xl bg-kyellow/40 border-2 border-kyellow px-4 py-2 text-xs font-bold text-cocoa">Add {MIN_ORDER_QTY - total} more packet{MIN_ORDER_QTY - total > 1 ? 's' : ''} — minimum order is {MIN_ORDER_QTY}.</p>}
            </div>

            <div className="rounded-[2rem] bg-white/70 border-2 border-cocoa p-6 space-y-3">
              <p className="font-display text-3xl text-cocoa leading-none">2 · YOUR DETAILS</p>
              <Field label="FULL NAME" testid="checkout-name" required value={form.name} onChange={set('name')} placeholder="Your name" />
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="PHONE NUMBER" testid="checkout-phone" required type="tel" value={form.phone} onChange={set('phone')} placeholder="+91 98765 43210" />
                <Field label="EMAIL" testid="checkout-email" required type="email" value={form.email} onChange={set('email')} placeholder="you@email.com" />
              </div>
              <label className="block">
                <span className="font-cond text-[10px] tracking-[0.3em] text-cocoa/60">DELIVERY ADDRESS</span>
                <textarea data-testid="checkout-address" required rows={3} value={form.address} onChange={set('address')} placeholder="House / street, area, city, pincode" className="k-input mt-1.5 !rounded-2xl resize-none" />
              </label>
            </div>
          </div>

          <div className="lg:sticky lg:top-28 rounded-[2rem] bg-cocoa text-cream border-2 border-cocoa p-6 shadow-[10px_10px_0_#E11D24]">
            <p className="font-display text-3xl leading-none">3 · PAY NOW</p>
            <div className="mt-5 space-y-2 text-sm">
              {FLAVOR_LIST.filter((f) => qty[f.slug] > 0).map((f) => (
                <div key={f.slug} className="flex justify-between"><span className="text-cream/70">{f.name} × {qty[f.slug]}</span><span>₹{qty[f.slug] * PACK.price}</span></div>
              ))}
              <div className="flex justify-between border-t border-cream/15 pt-3"><span className="text-cream/70">PACKETS</span><span data-testid="checkout-count">{total}</span></div>
              <div className="flex justify-between items-center"><span className="text-cream/70">SHIPPING</span><span data-testid="checkout-shipping" className="font-cond text-[10px] tracking-[0.2em] text-kyellow flex items-center gap-1"><Truck size={12} /> CHARGES APPLICABLE</span></div>
              <div className="flex justify-between items-end border-t border-cream/15 pt-3"><span className="font-cond tracking-[0.25em]">TOTAL</span><span data-testid="checkout-total" className="font-display text-5xl text-kyellow leading-none">₹{amount}</span></div>
              <p className="text-[10px] text-cream/50">+ shipping charges, confirmed on WhatsApp before dispatch.</p>
            </div>
            <label data-testid="checkout-policy-label" className="flex items-start gap-3 mt-5 cursor-pointer select-none">
              <input data-testid="checkout-policy-checkbox" type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5 w-5 h-5 shrink-0 accent-[#F7B500]" />
              <span className="text-xs leading-snug text-cream/80">I have read and accept all terms and conditions and policies of PROKRISP.</span>
            </label>
            <a data-testid="checkout-policy-link" href={POLICIES_PDF} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 mt-2 ml-8 font-cond font-bold text-[11px] tracking-[0.2em] text-kyellow hover:underline">CLICK TO VIEW HERE <ArrowUpRight size={13} /></a>
            <KrispButton testid="checkout-pay-now" type="submit" disabled={busy || !agreed} className="w-full mt-5 !py-4"><MessageCircle size={18} /> {busy ? 'OPENING...' : 'PAY NOW ON WHATSAPP →'}</KrispButton>
            <p className="font-cond text-[10px] tracking-[0.2em] text-cream/60 mt-4 flex items-center gap-2"><ShieldCheck size={13} className="text-kyellow" /> YOU'LL BE REDIRECTED TO WHATSAPP · {WHATSAPP.display}</p>
          </div>
        </form>
      </div>
      <Footer />
    </main>
  );
}
